import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { type ReactNode } from 'react';

import { SUPPORTED_CHAIN_IDS } from '@/constants/chain';

import { fontStyles } from '@/styles/typography';

import { ArrowUpRightIcon } from '@/ui/Icons/ArrowUpRightIcon';

import { classNames } from '@/utils/classnames';

import { routes } from '../../../utils/routes';
import {
  BlogIcon,
  CompanyIcon,
  CrossChainIcon,
  DeveloperApisIcon,
  LimitOrderIcon,
  LiquiditySourcesIcon,
  MatchaAutoIcon,
  SwapTokenIcon,
} from '../utils';
import animations from './animations.module.css';

interface LearnMoreDropdownListProps {
  hideBlogButton?: boolean;
  hideCompanyButton?: boolean;
  isInFeaturesPage?: boolean;
}

interface NavigationItem {
  id: string;
  title: string;
  description: string;
  href: string;
  icon: ReactNode;
  gradient: string;
  showExternalIcon?: boolean;
}

const iconStyle = {
  boxShadow:
    '0px 0.652px 1.955px 0px rgba(0, 0, 0, 0.12), 0px -0.652px 0px 0px rgba(0, 0, 0, 0.14) inset',
};

export const NavigationDropdownList = ({
  hideBlogButton = false,
  hideCompanyButton = false,
  isInFeaturesPage = false,
}: LearnMoreDropdownListProps) => {
  const featureLinkTarget = '_blank';

  const mainFeatures: NavigationItem[] = [
    {
      id: 'swap-tokens',
      title: 'Swap Tokens',
      description: 'Zero fee trades on 9+ million tokens',
      href: routes.SWAP_TOKENS,
      icon: <SwapTokenIcon />,
      gradient: 'linear-gradient(180deg, #F99704 0%, #F90 71.49%)',
      showExternalIcon: !isInFeaturesPage,
    },
    {
      id: 'gasless-swaps',
      title: 'Gasless Swaps',
      description: 'Go gasless with Matcha',
      href: routes.MATCHA_AUTO,
      icon: <MatchaAutoIcon />,
      gradient: 'linear-gradient(180deg, #009658 -20.83%, rgba(0, 185, 109, 0.60) 112.5%)',
      showExternalIcon: !isInFeaturesPage,
    },
    {
      id: 'liquidity-sources',
      title: 'Liquidity Sources',
      description: `130+ sources across ${SUPPORTED_CHAIN_IDS.length} chains`,
      href: routes.LIQUIDITY_SOURCES,
      icon: <LiquiditySourcesIcon />,
      gradient: 'linear-gradient(180deg, #309AFC 0%, #02A4FF 73.31%)',
      showExternalIcon: !isInFeaturesPage,
    },
    {
      id: 'limit-orders',
      title: 'Limit Orders',
      description: 'Set the price you want to pay',
      href: routes.LIMIT_ORDERS,
      icon: <LimitOrderIcon />,
      gradient: 'linear-gradient(180deg, #FF656D 0%, #F55E66 76.04%)',
      showExternalIcon: !isInFeaturesPage,
    },
    {
      id: 'cross-chain',
      title: 'Cross Chain',
      description: 'Swap across chains, in one trade',
      href: routes.CROSS_CHAIN_SWAPS,
      icon: <CrossChainIcon />,
      gradient: 'linear-gradient(180deg, #5932F3 0%, #4E5DE7 74.61%)',
      showExternalIcon: !isInFeaturesPage,
    },
  ];

  const companyItems: NavigationItem[] = [
    ...(!hideBlogButton
      ? [
          {
            id: 'blog',
            title: 'Blog',
            description: 'Stay up-to-date on the latest news',
            href: routes.BLOG,
            icon: <BlogIcon />,
            gradient: 'linear-gradient(180deg, #2CB051 0%, #59B56D 77.36%)',
            showExternalIcon: true,
          },
        ]
      : []),
    ...(!hideCompanyButton
      ? [
          {
            id: 'company',
            title: 'Company',
            description: 'Get to know Matcha',
            href: routes.ABOUT_MATCHA,
            icon: <CompanyIcon />,
            gradient: 'linear-gradient(180deg, #2CB051 0%, #59B56D 78%)',
            showExternalIcon: !isInFeaturesPage,
          },
        ]
      : []),
  ];

  const developerItems: NavigationItem[] = [
    {
      id: 'developer-apis',
      title: 'Developer APIs',
      description: 'Embed swaps into your app with 0x',
      href: `${routes.ZEROEX_HOME}docs/introduction/introduction-to-0x`,
      icon: <DeveloperApisIcon />,
      gradient: 'linear-gradient(180deg, #3F3F46 0%, #26272B 100%)',
      showExternalIcon: true,
    },
  ];

  const renderNavigationItem = ({
    id,
    title,
    description,
    href,
    icon,
    gradient,
    showExternalIcon,
  }: NavigationItem) => {
    const content = (
      <>
        <span
          className="rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0"
          style={{ background: gradient, ...iconStyle }}
        >
          {icon}
        </span>
        <div className="ml-3 flex-1">
          <div
            className={fontStyles({
              variant: 'LABEL_MD',
              class: ['text-token-gray-1600', 'flex', 'items-center', 'gap-1'],
            })}
          >
            {title}
            {showExternalIcon && (
              <ArrowUpRightIcon
                className={classNames(
                  animations['arrow-up-right-icon'],
                  animations['animate-arrow-hover'],
                )}
              />
            )}
          </div>
          <div
            className={fontStyles({
              variant: 'BODY_MD',
              class: ['text-token-gray-800', 'whitespace-nowrap'],
            })}
          >
            {description}
          </div>
        </div>
      </>
    );

    return (
      <DropdownMenu.Item key={id} asChild>
        <a
          target={featureLinkTarget}
          href={href}
          className="py-4 px-3 flex items-center hover:bg-token-gray-200 transition-colors duration-150 cursor-pointer focus:outline-none focus:bg-token-gray-200 rounded-lg"
        >
          {content}
        </a>
      </DropdownMenu.Item>
    );
  };

  const renderDivider = () => (
    <hr className="border-special-border-divider border-0 border-t border-solid -mx-1.5 my-1" />
  );

  return (
    <div className="bg-special-background-modal">
      {/* Main Features */}
      {mainFeatures.map(renderNavigationItem)}

      {/* Company Section */}
      {companyItems.length > 0 && (
        <>
          {renderDivider()}
          {companyItems.map(renderNavigationItem)}
        </>
      )}

      {/* Developer Section */}
      {renderDivider()}
      {developerItems.map(renderNavigationItem)}
    </div>
  );
};
